import React, { useState, useEffect } from 'react';
import { Grid, Typography, Box, TextField, CircularProgress, IconButton } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Modal from '../../../components/Modal';
import SbButton from '../../../components/Button';

interface ImportAiModalProps {
  open: boolean;
  onClose: () => void;
  isImporting: boolean;
  onUploadClick: () => void;
  onExtractText: (text: string) => void;
  error: string | null;
  setError: (error: string | null) => void;
}

const SAMPLE_SHIPMENT_DETAILS = `TAX INVOICE MEH-2214 · Meher Textiles Pvt Ltd, Plot 18 GIDC Pandesara, Surat 395003 · Ship to: Nova Retail Pvt Ltd, Orion Mall, Dr Rajkumar Road, Bengaluru 560055 · 200 pcs Men’s Cotton T-Shirts (HSN 6109) · Gross wt 42 kg · Value ₹48,500`;

function ImportAiModal({
  open,
  onClose,
  isImporting,
  onUploadClick,
  onExtractText,
  error,
  setError,
}: ImportAiModalProps) {
  const [textInput, setTextInput] = useState('');

  // Reset inputs when opened
  useEffect(() => {
    if (open) {
      setTextInput('');
      setError(null);
    }
  }, [open, setError]);

  const handleExtractClick = () => {
    if (!textInput.trim()) {
      setError('Please enter some shipment details first.');
      return;
    }
    onExtractText(textInput);
  };

  return (
    <Modal open={open} onClose={isImporting ? undefined : onClose} maxWidth="md">
      <style>{`
        @keyframes modalGeminiGlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: '#7c4dff' }} />
          <Typography variant="h6" className="font-bold" sx={{ color: '#333' }}>
            Import with AI
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={isImporting} size="small" sx={{ color: '#666' }}>
          <HighlightOffIcon />
        </IconButton>
      </Box>

      {/* Error state */}
      {error && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: '12px',
            backgroundColor: 'rgba(211, 47, 47, 0.08)',
            border: '1px solid rgba(211, 47, 47, 0.2)',
          }}
        >
          <Typography color="error" variant="body2" className="font-medium">
            ⚠️ {error}
          </Typography>
        </Box>
      )}

      {/* Options content */}
      <Grid container spacing={4} sx={{ position: 'relative' }}>
        {/* Upload File */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              height: '100%',
              minHeight: '260px',
              border: '2px dashed rgba(124, 77, 255, 0.3)',
              borderRadius: '16px',
              backgroundColor: 'rgba(124, 77, 255, 0.02)',
              cursor: isImporting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              opacity: isImporting ? 0.6 : 1,
              '&:hover': {
                borderColor: isImporting ? 'rgba(124, 77, 255, 0.3)' : '#7c4dff',
                backgroundColor: isImporting ? 'rgba(124, 77, 255, 0.02)' : 'rgba(124, 77, 255, 0.05)',
                transform: isImporting ? 'none' : 'translateY(-2px)',
              },
            }}
            onClick={isImporting ? undefined : onUploadClick}
          >
            <CloudUploadIcon sx={{ fontSize: '48px', color: '#7c4dff', mb: 2 }} />
            <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: '#333' }}>
              Upload File
            </Typography>
            <Typography variant="body2" className="text-center mb-4" sx={{ color: '#666', maxWidth: '200px' }}>
              Upload invoice, packing list, or other shipment documents.
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', textAlign: 'center' }}>
              Supported formats: XLSX, XLS, PDF, PNG, JPG, JPEG, DOC, DOCX, TXT, CSV
            </Typography>
          </Box>
        </Grid>

        {/* Divider for MD and up */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            left: '50%',
            top: '10%',
            bottom: '10%',
            width: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            transform: 'translateX(-50%)',
          }}
        />

        {/* Enter Details Manually */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: '260px',
              opacity: isImporting ? 0.6 : 1,
            }}
          >
            <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: '#333' }}>
              Enter Details Manually
            </Typography>
            <Typography variant="body2" className="mb-3" sx={{ color: '#666' }}>
              Paste invoice details, customer info, or package specs in natural language.
            </Typography>

            <TextField
              multiline
              rows={6}
              placeholder="Paste shipment/customer details here... (e.g., invoice contents, billing/shipping address, item counts, weights, values)"
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              disabled={isImporting}
              fullWidth
              variant="outlined"
              sx={{
                mb: 2,
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.24)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
              <Typography
                variant="caption"
                onClick={() => {
                  if (!isImporting) {
                    setTextInput(SAMPLE_SHIPMENT_DETAILS);
                  }
                }}
                sx={{
                  color: 'primary.main',
                  cursor: isImporting ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: 'action.hover',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '16px',
                  border: '1px dashed',
                  borderColor: 'primary.light',
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  opacity: isImporting ? 0.6 : 1,
                  '&:hover': {
                    backgroundColor: isImporting ? 'action.hover' : 'action.selected',
                    borderColor: isImporting ? 'primary.light' : 'primary.main',
                  },
                }}
              >
                ✨ Try with sample details
              </Typography>
            </Box>

            <SbButton
              onClick={handleExtractClick}
              disabled={isImporting || !textInput.trim()}
              variant="contained"
              color="primary"
              sx={{
                borderRadius: '8px !important',
                height: '40px',
                alignSelf: 'flex-end',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Extract Details
            </SbButton>
          </Box>
        </Grid>
      </Grid>

      {/* Global loading overlay */}
      {isImporting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(4px)',
            borderRadius: '20px',
            zIndex: 10,
          }}
        >
          <CircularProgress size={50} sx={{ color: '#7c4dff', mb: 2 }} />
          <Typography
            variant="h6"
            className="font-bold animate-pulse"
            sx={{
              background: 'linear-gradient(270deg, #7c4dff, #ff4081, #00e5ff, #7c4dff)',
              backgroundSize: '400% 400%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'modalGeminiGlow 3s ease infinite, pulse 2s infinite',
            }}
          >
            AI is analyzing details...
          </Typography>
        </Box>
      )}
    </Modal>
  );
}

export default ImportAiModal;
